const ENTRY_SELECTOR = "[data-rsv-vstudio-entry]";
const FILE_SELECTOR = "[data-rsv-vstudio-file]";
const STATUS_SELECTOR = "[data-rsv-vstudio-status]";
const DEFAULT_SOURCE = "sitewide-upload";
const API_WAIT_TIMEOUT_MS = 8000;
const API_WAIT_INTERVAL_MS = 40;

export function firstDroppedFile(dataTransfer) {
  return dataTransfer?.files?.[0] ?? null;
}

export function normalizeEntrySource(value) {
  const source = typeof value === "string" ? value.trim() : "";
  return source || DEFAULT_SOURCE;
}

export function handoffPhotoFile(file, api, source = DEFAULT_SOURCE) {
  return api.openWithPhoto(file, {
    source: normalizeEntrySource(source)
  });
}

export function waitForVStudioPhotoApi(
  host = window,
  {
    timeoutMs = API_WAIT_TIMEOUT_MS,
    intervalMs = API_WAIT_INTERVAL_MS
  } = {}
) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const check = () => {
      const api = host.RSVVStudio;
      if (api && typeof api.openWithPhoto === "function") {
        resolve(api);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error("V Studio is still loading. Please try again."));
        return;
      }
      host.setTimeout(check, intervalMs);
    };
    check();
  });
}

function setEntryState(entry, state, message) {
  entry.dataset.rsvVstudioState = state;
  entry.setAttribute("aria-busy", state === "busy" ? "true" : "false");
  const status = entry.querySelector(STATUS_SELECTOR);
  if (status) status.textContent = message;
}

async function openEntryFile(entry, input, file, host) {
  if (!file || entry.dataset.rsvVstudioState === "busy") return;
  setEntryState(entry, "busy", "Opening V Studio with your photo…");
  input.disabled = true;

  try {
    const api = await waitForVStudioPhotoApi(host);
    // The native file picker can leave focus on <body>. Put it back on the
    // originating control so V Studio's existing focus-return contract can
    // restore the customer to the same sitewide entry point on close.
    entry.focus({ preventScroll: true });
    const result = await handoffPhotoFile(
      file,
      api,
      entry.dataset.rsvVstudioSource
    );
    if (!result?.ok) {
      throw new Error(result?.message || "V Studio could not open that photo.");
    }
    setEntryState(entry, "ready", "Photo added to V Studio.");
  } catch (error) {
    setEntryState(
      entry,
      "error",
      error instanceof Error
        ? error.message
        : "V Studio could not open that photo."
    );
  } finally {
    input.disabled = false;
    input.value = "";
  }
}

export function initializeVStudioEntry(entry, host = window) {
  if (!(entry instanceof HTMLElement)) return false;
  if (entry.dataset.rsvVstudioInitialized === "true") return true;
  const input = entry.querySelector(FILE_SELECTOR);
  if (!(input instanceof HTMLInputElement) || input.type !== "file") {
    return false;
  }

  entry.dataset.rsvVstudioInitialized = "true";
  entry.dataset.rsvVstudioState = "idle";
  let dragDepth = 0;

  input.addEventListener("change", () => {
    const file = input.files?.[0];
    if (file) void openEntryFile(entry, input, file, host);
  });

  entry.addEventListener("keydown", (event) => {
    if (event.target !== entry) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    input.click();
  });

  entry.addEventListener("dragenter", (event) => {
    if (!event.dataTransfer?.types?.includes("Files")) return;
    event.preventDefault();
    dragDepth += 1;
    entry.dataset.rsvVstudioDrag = "active";
  });

  entry.addEventListener("dragover", (event) => {
    if (!event.dataTransfer?.types?.includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  });

  entry.addEventListener("dragleave", () => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) entry.dataset.rsvVstudioDrag = "idle";
  });

  entry.addEventListener("drop", (event) => {
    event.preventDefault();
    dragDepth = 0;
    entry.dataset.rsvVstudioDrag = "idle";
    const file = firstDroppedFile(event.dataTransfer);
    if (file) void openEntryFile(entry, input, file, host);
  });

  return true;
}

export function initializeVStudioEntries(root = document, host = window) {
  const entries = root.querySelectorAll?.(ENTRY_SELECTOR) ?? [];
  entries.forEach((entry) => initializeVStudioEntry(entry, host));
  return entries.length;
}

function boot() {
  initializeVStudioEntries();
  document.addEventListener("shopify:section:load", (event) => {
    initializeVStudioEntries(event.target);
  });
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
}
